/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import {
  CheckCircleOutlined,
  QuestionCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { status } from '@sportninja/common/constants/attendanceStatus'
import { Tooltip } from 'antd'
import React from 'react'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import {
  ATTENDANCE_STATUS_COPY_KEY_BY_VALUE,
  getAttendanceStatusTooltip,
} from '../utils/statusCopy'

const ICONS = [
  {
    value: status.ATTENDING,
    Icon: CheckCircleOutlined,
    color: colors.SUCCESS_300,
  },
  {
    value: status.MAYBE,
    Icon: QuestionCircleOutlined,
    color: colors.WARNING_300,
  },
  {
    value: status.NOT_ATTENDING,
    Icon: CloseCircleOutlined,
    color: colors.ERROR_300,
  },
]

export interface AttendanceAvailIconsProps {
  attendanceStatus?: number | null
  disabled?: boolean
  // Shows the current status at full color but takes no input and no tooltip —
  // unlike `disabled`, which dims the icons to flag a locked player.
  readOnly?: boolean
  yesNoOnly?: boolean
  // Per-status tooltip copy that replaces the default for that icon.
  tooltipByValue?: Partial<Record<number, string>>
  onPress: (next: number | null) => void
}

const rowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
`
const iconStyle = (
  active: boolean,
  color: string,
  disabled: boolean,
  readOnly: boolean
) => css`
  font-size: 20px;
  cursor: ${readOnly ? 'default' : disabled ? 'not-allowed' : 'pointer'};
  color: ${active ? color : colors.NEUTRAL_100};
  opacity: ${disabled ? 0.5 : 1};
`

const AttendanceAvailIcons: React.FC<AttendanceAvailIconsProps> = ({
  attendanceStatus,
  disabled = false,
  readOnly = false,
  yesNoOnly = false,
  tooltipByValue,
  onPress,
}) => {
  const icons = ICONS.filter((i) => !yesNoOnly || i.value !== status.MAYBE)
  return (
    <div css={rowStyle}>
      {icons.map(({ value, Icon, color }) => {
        const active = attendanceStatus === value
        const tooltip =
          tooltipByValue?.[value] ??
          getAttendanceStatusTooltip(ATTENDANCE_STATUS_COPY_KEY_BY_VALUE[value])
        return (
          <Tooltip
            key={value}
            // An empty title keeps antd's tooltip closed.
            title={readOnly ? undefined : tooltip}
            zIndex={MODAL_TOKENS.Z_INDEX_TOOLTIP}
          >
            <Icon
              css={iconStyle(active, color, disabled, readOnly)}
              aria-label={tooltip}
              // No handler in read-only: an icon with onClick gets a tabindex,
              // and antd's `.anticon[tabindex]` forces a pointer cursor.
              onClick={
                readOnly
                  ? undefined
                  : () => {
                      if (disabled) {
                        return
                      }
                      onPress(active ? null : value)
                    }
              }
            />
          </Tooltip>
        )
      })}
    </div>
  )
}

export default AttendanceAvailIcons
